import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import {
  ArrowUp,
  ChevronRight,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";

import "../styles/GalaxyAssistant.css";

/* =========================================
   API CONFIGURATION

   Production backend:
   https://galaxyedgeserver.vercel.app

   If REACT_APP_API_URL exists in Vercel,
   it will use that instead.
========================================= */

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://galaxyedgeserver.vercel.app";

/* =========================================
   QUICK PROMPTS
========================================= */

const QUICK_PROMPTS = [
  "I need safety training",
  "Help me find the right solution",
  "What services does GalaxyEDGE offer?",
  "I'm not sure what I need",
];

/* =========================================
   INITIAL MESSAGE
========================================= */

const INITIAL_MESSAGE = {
  role: "assistant",
  content:
    "Tell me what your organization needs help with. I’ll use GalaxyEDGE information to guide you toward the right solution.",
};

/* =========================================
   COMPONENT
========================================= */

export default function GalaxyAssistant() {
  const [isOpen, setIsOpen] = useState(false);

  const [input, setInput] = useState("");

  const [messages, setMessages] = useState([
    INITIAL_MESSAGE,
  ]);

  const [isThinking, setIsThinking] =
    useState(false);

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  /* =========================================
     AUTO SCROLL
  ========================================= */

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages, isThinking]);

  /* =========================================
     FOCUS INPUT
  ========================================= */

  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 500);

    return () => clearTimeout(timer);
  }, [isOpen]);

  /* =========================================
     SEND MESSAGE
  ========================================= */

  const sendMessage = async (
    messageOverride = null
  ) => {
    const message = (
      messageOverride ?? input
    ).trim();

    if (!message || isThinking) return;

    const userMessage = {
      role: "user",
      content: message,
    };

    /*
      Only send recent conversation history.

      The backend also trims history,
      but keeping this small reduces payload size.
    */

    const history = messages
      .slice(-6)
      .map(({ role, content }) => ({
        role,
        content,
      }));

    setMessages((current) => [
      ...current,
      userMessage,
    ]);

    setInput("");

    setIsThinking(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/assistant`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify({
            message,
            history,
          }),
        }
      );

      /*
        Handle rate limiting separately
        instead of showing a generic error.
      */

      if (response.status === 429) {
        setMessages((current) => [
          ...current,
          {
            role: "assistant",

            content:
              "GalaxyEDGE AI is receiving several requests right now. Please wait a few seconds and try again.",

            resources: [],
            error: true,
          },
        ]);

        return;
      }

      if (!response.ok) {
        const errorText =
          await response.text();

        console.error(
          "GalaxyEDGE AI server response:",
          errorText
        );

        throw new Error(
          `Assistant request failed: ${response.status}`
        );
      }

      const data =
        await response.json();

      console.log(
        "GalaxyEDGE AI response:",
        data
      );

      setMessages((current) => [
        ...current,
        {
          role: "assistant",

          content:
            data.answer ||
            "I couldn't find enough GalaxyEDGE information to answer that.",

          resources:
            Array.isArray(
              data.resources
            )
              ? data.resources
              : [],
        },
      ]);
    } catch (error) {
      console.error(
        "GalaxyEDGE AI error:",
        error
      );

      setMessages((current) => [
        ...current,
        {
          role: "assistant",

          content:
            "I’m having trouble reaching the GalaxyEDGE assistant right now. Please try again.",

          resources: [],
          error: true,
        },
      ]);
    } finally {
      setIsThinking(false);
    }
  };

  /* =========================================
     KEYBOARD
  ========================================= */

  const handleKeyDown = (event) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey
    ) {
      event.preventDefault();

      sendMessage();
    }
  };

  /* =========================================
     UI
  ========================================= */

  return (
    <>
      {/* =====================================
          FULL VIEWPORT SIRI GLOW
      ====================================== */}

      <div
        className={`galaxy-screen-glow ${
          isOpen ? "active" : ""
        }`}
        aria-hidden="true"
      />

      {/* =====================================
          FLOATING LAUNCHER
      ====================================== */}

      <button
        type="button"
        className={`galaxy-ai-launcher ${
          isOpen ? "active" : ""
        }`}
        onClick={() =>
          setIsOpen(
            (current) => !current
          )
        }
        aria-label={
          isOpen
            ? "Close GalaxyEDGE AI"
            : "Open GalaxyEDGE AI"
        }
      >
        <div className="launcher-orb">
          <span className="launcher-ring launcher-ring-one" />

          <span className="launcher-ring launcher-ring-two" />

          <span className="launcher-ring launcher-ring-three" />

          <span className="launcher-core" />
        </div>

        <div className="launcher-copy">
          <span className="launcher-eyebrow">
            GALAXYEDGE AI
          </span>

          <span className="launcher-title">
            Ask GalaxyEDGE
          </span>
        </div>
      </button>

      {/* =====================================
          ASSISTANT
      ====================================== */}

      <div
        className={`galaxy-assistant-shell ${
          isOpen ? "visible" : ""
        }`}
      >
        <section className="galaxy-assistant-window">

          {/* HEADER */}

          <header className="assistant-header">
            <div className="assistant-brand">

              <div className="assistant-brand-orb">
                <span className="brand-ring brand-ring-one" />

                <span className="brand-ring brand-ring-two" />

                <span className="brand-core" />
              </div>

              <div className="assistant-brand-copy">

                <div className="assistant-brand-name">
                  GalaxyEDGE
                  <span> AI</span>
                </div>

                <div className="assistant-status">
                  <span className="assistant-status-dot" />

                  Intelligent Safety Assistant
                </div>

              </div>
            </div>

            <button
              type="button"
              className="assistant-close"
              onClick={() =>
                setIsOpen(false)
              }
              aria-label="Close assistant"
            >
              <X size={18} />
            </button>
          </header>

          {/* INTRO */}

          <div className="assistant-intro">

            <div className="assistant-eyebrow">
              <Sparkles size={13} />

              INTELLIGENT GUIDANCE
            </div>

            <h2>
              What can we
              <span> solve?</span>
            </h2>

            <p>
              Tell us what your team needs.
              GalaxyEDGE AI will search company
              information and guide you toward
              the right resource.
            </p>

          </div>

          {/* CHAT */}

          <div className="assistant-chat">

            {messages.map(
              (message, index) => (
                <div
                  key={`${message.role}-${index}`}
                  className={`assistant-message-row ${message.role}`}
                >

                  {message.role ===
                    "assistant" && (
                    <div className="assistant-avatar">
                      <Sparkles
                        size={12}
                      />
                    </div>
                  )}

                  <div
                    className={`assistant-message ${message.role}`}
                  >

                    {message.role ===
                      "assistant" && (
                      <div className="assistant-message-name">
                        GALAXYEDGE AI
                      </div>
                    )}

                    <p>
                      {message.content}
                    </p>

                    {!!message.resources
                      ?.length && (
                      <div className="assistant-resources">

                        {message.resources.map(
                          (
                            resource,
                            resourceIndex
                          ) => (
                            <a
                              href={
                                resource.url
                              }
                              key={`${resource.title}-${resourceIndex}`}
                              className="assistant-resource"
                            >

                              <div className="resource-copy">

                                <span className="resource-label">
                                  RECOMMENDED
                                </span>

                                <strong>
                                  {
                                    resource.title
                                  }
                                </strong>

                              </div>

                              <span className="resource-arrow">
                                <ChevronRight
                                  size={16}
                                />
                              </span>

                            </a>
                          )
                        )}

                      </div>
                    )}

                  </div>
                </div>
              )
            )}

            {/* THINKING */}

            {isThinking && (
              <div className="assistant-message-row assistant">

                <div className="assistant-avatar">
                  <Sparkles
                    size={12}
                  />
                </div>

                <div className="assistant-thinking">
                  <span />
                  <span />
                  <span />
                </div>

              </div>
            )}

            <div ref={chatEndRef} />

          </div>

          {/* QUICK START */}

          {messages.length === 1 && (
            <div className="assistant-quick-section">

              <span className="assistant-quick-label">
                QUICK START
              </span>

              <div className="assistant-quick-grid">

                {QUICK_PROMPTS.map(
                  (prompt) => (
                    <button
                      type="button"
                      key={prompt}
                      onClick={() =>
                        sendMessage(
                          prompt
                        )
                      }
                    >
                      <span>
                        {prompt}
                      </span>

                      <ChevronRight
                        size={14}
                      />
                    </button>
                  )
                )}

              </div>
            </div>
          )}

          {/* INPUT */}

          <div className="assistant-input-area">

            <div className="assistant-input">

              <div className="input-glow" />

              <textarea
                ref={inputRef}
                value={input}
                onChange={(event) =>
                  setInput(
                    event.target.value
                  )
                }
                onKeyDown={
                  handleKeyDown
                }
                placeholder="Ask GalaxyEDGE anything..."
                rows={1}
                maxLength={2000}
              />

              <button
                type="button"
                className="assistant-send"
                onClick={() =>
                  sendMessage()
                }
                disabled={
                  !input.trim() ||
                  isThinking
                }
                aria-label="Send message"
              >
                <ArrowUp
                  size={18}
                />
              </button>

            </div>

            <div className="assistant-trust">

              <ShieldCheck
                size={11}
              />

              <span>
                Powered by approved
                GalaxyEDGE information
              </span>

            </div>

          </div>

        </section>
      </div>
    </>
  );
}